import { inject, Injectable } from '@angular/core'

import { sortBy } from 'lodash-es'
import { combineLatest, firstValueFrom, map, Observable, of, shareReplay, takeUntil } from 'rxjs'

import {
  Action,
  AppFeatures,
  DataQueryProps,
  FLEET_CUSTOM_ROLE_ID_RANGE,
  FLEET_ROLE_ID_RANGE,
  Group,
  MfaMethod,
  MyProfile,
  Resource,
  Role,
  RoleRange,
  UserProfile,
  UserRole,
} from '@ti/contracts'
import { AuthSession } from '@ti/core/auth/services/auth.session'
import { FilterData, FilterOption } from '@ti/core/common/contracts'
import { Memoize } from '@ti/core/common/decorators'
import { AccessControl, PricingPlanService } from '@ti/core/common/services'
import { canManageUserGroups, isGroupRestricted } from '@ti/core/common/utils'
import {
  DataGridColumnAction,
  DataGridColumnActionsFn,
  DataGridColumnType,
} from '@ti/core/contracts/data-grid'
import { DIALOG_FACADE } from '@ti/core/contracts/dialog'
import { ListModel } from '@ti/core/models/list.model'
import { GetPagesDataDto, ListModelConfig, ListModelState } from '@ti/core/models/types'
import { HIDDEN_FLEET_ROLES } from '@ti/shared/roles/constants'
import { UserRolesProvider } from '@ti/shared/roles/providers'
import { RolesManager, RoleStore } from '@ti/shared/roles/services'

import { BaseUserManager } from '../services/user.manager'

export type UserGridData = {
  user: UserProfile
  role: string
  groupsAccess: Group[]
  lastActivity: {
    type: string
    date: Date | string
  }
}

export type UsersListState = ListModelState<UserProfile> & {
  gridData$: Observable<UserGridData[] | null>
}

@Injectable()
export abstract class BaseUsersModel extends ListModel<UserProfile, UsersListState> {
  public hideCurrentUser = true

  protected readonly accessControl = inject(AccessControl)
  protected readonly rolesManager = inject(RolesManager)
  protected readonly roleStore = inject(RoleStore)
  protected readonly authSession = inject(AuthSession)
  protected readonly dialogFacade = inject(DIALOG_FACADE)
  protected readonly userOptionsProvider = inject(UserRolesProvider)
  protected readonly userManager = inject(BaseUserManager)
  protected readonly pricingPlanService = inject(PricingPlanService)

  protected abstract modelName: string

  /** Built-in and custom role id ranges the listed users belong to */
  protected readonly roleIdRanges: RoleRange[] = [FLEET_ROLE_ID_RANGE, FLEET_CUSTOM_ROLE_ID_RANGE]

  protected override async init() {
    await super.init()
    this.userManager.events$.updated.pipe(takeUntil(this.destroy$)).subscribe(() => this.refresh())

    this.roleFilterOptions$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateFilterOptions())
  }

  protected override loadState(): UsersListState {
    const data = super.loadState()
    return {
      ...data,
      gridData$: combineLatest([
        this.authSession.state.currentUserAttributes$,
        data.items$,
        this.roleStore.state$.items,
        this.userOptionsProvider.userRoleLabels$,
        this.groupsAccessData$,
      ]).pipe(
        takeUntil(this.destroy$),
        map(([currentUser, users, roles, roleLabels, groups]) => {
          return users
            .filter((user) => {
              return this.hideCurrentUser ? user.id !== currentUser?.sub : true
            })
            .map((user) => ({
              ...this.mapUserData(user, roles, roleLabels),
              groupsAccess: this.mapGroupsAccess(user, groups),
            }))
        }),
      ),
    } as UsersListState
  }

  /**
   * Groups the `groupsAccess` ids are resolved against. Groups are a fleet concept, so only
   * fleet-scoped models provide them - everywhere else the column stays empty and is hidden
   * by the pricing plan check anyway.
   */
  protected get groupsAccessData$(): Observable<Group[]> {
    return of([])
  }

  /** True while the fleet pricing plan includes groups, which gates the column and the filter */
  @Memoize()
  protected get groupsAndLabelsAllowed$(): Observable<boolean> {
    return this.pricingPlanService
      .areFeaturesAllowed$([AppFeatures.GroupsAndLabels])
      .pipe(takeUntil(this.destroy$), shareReplay({ bufferSize: 1, refCount: false }))
  }

  /**
   * `groupsAccess` is `null`/`undefined` for unrestricted users, who are not limited to any
   * group and therefore render without tags.
   */
  protected mapGroupsAccess(user: UserProfile, groups: Group[]): Group[] {
    if (!isGroupRestricted(user.groupsAccess)) {
      return []
    }
    return groups.filter((group) => user.groupsAccess?.includes(group.id))
  }

  protected override createFilteredColumnsStream() {
    return combineLatest([super.createFilteredColumnsStream(), this.groupsAndLabelsAllowed$]).pipe(
      takeUntil(this.destroy$),
      map(([columns, groupsAllowed]) => {
        return groupsAllowed ? columns : columns.filter((column) => column.field !== 'groupsAccess')
      }),
    )
  }

  protected mapUserData(user: UserProfile, roles: Role[], roleLabels: Record<UserRole, string>) {
    return {
      user: user,
      role: this.rolesManager.isCustom(user.roleId)
        ? roles.find((role) => role.id === user.roleId)?.name
        : roleLabels[user.roleId],
      mfa: user.mfaStatus?.length,
      lastActivity: user.inviteAcceptedAt
        ? {
            type: 'login',
            date: user.inviteAcceptedAt,
          }
        : {
            type: 'invite',
            date: user.invitedAt as Date,
          },
    }
  }

  protected config(): ListModelConfig {
    return {
      name: this.modelName,
      loadFilterOptions: () => this.loadFilterOptions(),
      applyRequestFilters: (queryProps, filters) => this.applyRequestFilters(queryProps, filters),
      gridColumns: [
        {
          field: 'user',
          label: 'users.grid.user',
          type: DataGridColumnType.Template,
          sortable: true,
        },
        {
          field: 'auditId',
          label: 'ID',
          type: DataGridColumnType.Text,
          sortable: true,
        },
        {
          field: 'groupsAccess',
          label: 'users.form.group-access',
          type: DataGridColumnType.Tags,
          visibleTags: 2,
          sortable: false,
          placeholder: '--',
        },
        {
          field: 'role',
          label: 'users.grid.role',
          type: DataGridColumnType.Text,
          sortable: true,
        },
        {
          field: 'mfa',
          label: 'users.grid.mfa',
          type: DataGridColumnType.Template,
          sortable: true,
        },
        {
          field: 'lastActivity',
          // @TODO change label back when activity tracking will be implemented
          // label: 'users.grid.last-activity',
          label: 'users.grid.created-at',
          type: DataGridColumnType.Template,
          sortable: true,
        },
        {
          field: 'loginAs',
          label: '',
          type: DataGridColumnType.Template,
          sortable: false,
        },
        {
          field: 'actions',
          label: '',
          type: DataGridColumnType.Actions,
          actions: ((data: UserGridData) => this.getOptionsStream(data)) as DataGridColumnActionsFn,
        },
      ],
      defaultOrderColumn: 'lastActivity',
      defaultOrderDirection: 'DESC',
    }
  }

  protected async loadCount() {
    const count = await this.countAllUsers()
    return count - (this.hideCurrentUser ? 1 : 0)
  }

  protected async loadFilterOptions(): Promise<FilterData[]> {
    return Promise.all([this.createRoleFilterOption(), this.createMfaFilterOption()])
  }

  protected async createRoleFilterOption(): Promise<FilterData> {
    return {
      key: 'roleId',
      type: 'list-multiselect',
      title: 'users.grid.role',
      options: await firstValueFrom(this.roleFilterOptions$),
      searchable: true,
    }
  }

  protected async createMfaFilterOption(): Promise<FilterData> {
    const labels = await this.languageService.massTranslate({
      enabled: 'common.enabled',
      disabled: 'common.disabled',
    })

    return {
      key: 'mfaStatus',
      type: 'list-multiselect',
      title: 'users.grid.mfa',
      options: [
        { label: labels.enabled, value: MfaMethod.TOTP },
        { label: labels.disabled, value: null },
      ],
      searchable: false,
    }
  }

  @Memoize()
  protected get roleFilterOptions$(): Observable<FilterOption[]> {
    return combineLatest([
      this.userOptionsProvider.userRoleLabels$,
      this.roleStore.state$.items,
    ]).pipe(
      takeUntil(this.destroy$),
      map(([roleLabels, roles]) => {
        // Remove hidden roles from the list
        roles = roles.filter((role) => !HIDDEN_FLEET_ROLES.includes(role.id))

        return sortBy(
          roles
            .filter((role) => this.isRoleInScope(role.id))
            .map((role) => ({
              label: roleLabels[role.id as UserRole] || role.name,
              value: role.id,
            })),
          'label',
        )
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    )
  }

  protected isRoleInScope(roleId: number): boolean {
    return this.roleIdRanges.some(([from, to]) => roleId >= from && roleId <= to)
  }

  protected applyRequestFilters(queryProps: DataQueryProps, filters?: Record<string, any> | null) {
    if (!filters || !Object.getOwnPropertyNames(filters).length) {
      return
    }

    // Never mutate the stored filter value
    const preparedFilters: Record<string, any> = { ...filters }

    // `mfa_status` is an array column, so `and` matches an enrolled method. The backend pairs
    // it with an "empty array" check whenever the list contains `null`, which is exactly the
    // "two-factor authentication disabled" option.
    if (Array.isArray(preparedFilters.mfaStatus)) {
      preparedFilters.mfaStatus = { and: preparedFilters.mfaStatus }
    }

    queryProps.filter = preparedFilters
  }

  protected override initExportColumns() {
    return [
      {
        field: 'name',
        labelTranslation: 'users.grid.user',
      },
      {
        field: 'email',
        labelTranslation: 'users.form.email',
      },
      {
        field: 'role',
        labelTranslation: 'users.grid.role',
      },
      {
        field: 'createdAt',
        labelTranslation: 'users.grid.created-at',
      },
    ]
  }

  protected override async convertDataToExport(data: UserProfile[]) {
    const roleLabels = await firstValueFrom(this.userOptionsProvider.userRoleLabels$)
    const roles = await firstValueFrom(this.roleStore.state$.items)
    return data.map((user) => {
      return {
        name: user.name,
        email: user.email,
        role: this.rolesManager.isCustom(user.roleId)
          ? roles.find((role) => role.id === user.roleId)?.name
          : roleLabels[user.roleId],
        createdAt: this.dateFormatService.formatDateInstant(
          user.inviteAcceptedAt || user.createdAt,
          'grid-date-time-sec',
        ),
      }
    })
  }

  protected getOptionsStream(data: UserGridData): Observable<DataGridColumnAction[]> {
    return combineLatest([
      this.profile.state,
      this.accessControl.massCheck$([
        [Resource.User, Action.Update],
        [Resource.User, Action.Delete],
      ]),
    ]).pipe(
      map(([user, [canUpdate, canDelete]]) => {
        if (this.shouldDisableRowOptions(data.user, user)) {
          return []
        }
        // Group-restricted users may only manage users whose groups are within their own
        const canManageTarget = canManageUserGroups(user.groupsAccess, data.user.groupsAccess)
        const allowUpdate = canUpdate && canManageTarget
        const allowDelete = canDelete && canManageTarget
        return [
          {
            label: 'users.grid.edit-btn',
            testId: 'users.grid.edit-btn',
            icon: 'pi pi-pencil',
            styleClass: `has-bottom-border ${allowUpdate ? '' : 'not-allowed'}`,
            command: () => (allowUpdate ? this.select(data.user) : undefined),
          },
          {
            label: 'users.grid.delete-btn',
            testId: 'users.grid.delete-btn',
            icon: 'pi pi-trash',
            styleClass: `error ${allowDelete ? '' : 'not-allowed'}`,
            command: () => (allowDelete ? this.userManager.delete(data.user) : undefined),
          },
        ]
      }),
      takeUntil(this.destroy$),
      shareReplay({ bufferSize: 1, refCount: false }),
    )
  }

  protected abstract countAllUsers(): Promise<number>

  protected abstract override loadPage(
    props: DataQueryProps,
  ): Promise<UserProfile[]> | Observable<UserProfile[]>

  protected override getPageData(page: number, dto: GetPagesDataDto) {
    // Override column names in sort
    if (dto.orderBy?.column === 'mfa') {
      dto.orderBy.column = 'mfaStatus'
    }
    if (dto.orderBy?.column === 'lastActivity') {
      dto.orderBy.column = 'createdAt'
    }
    return super.getPageData(page, dto)
  }

  protected shouldDisableRowOptions(targetUser: UserProfile, currentUser: MyProfile) {
    return (
      (targetUser.roleId === UserRole.FleetOwner ||
        targetUser.roleId === UserRole.TSPOwner ||
        targetUser.roleId === UserRole.TIOwner) &&
      currentUser.roleId !== UserRole.FleetDemo
    )
  }
}
